import React, { useEffect } from 'react'
import Svg from '../../../../../../component/common/Svg';
import Input from "../../../../../../core/Inputs/DefaultInput";
import Switch from "../../../../../../core/Switch";
import style from "../../../../../style.module.css";
import Card from "../../../../../../core/Card";
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import { updateField } from '../../../../../../store/actions/formBuilderAction';
import { useTranslation } from 'react-i18next';


const EditModal = ({ data, modalOpen, setModalOpen, language_code, survey_id }) => {

    //==> STATES <==
    const { field, label, name, type, id, options } = data ?? {}
    const dispatch = useDispatch()
    const { t, i18n } = useTranslation();

    //==> FORM SCHEMA AND VALIDATION <==

    //Yup
    let shape = {
        isLabel: Yup.boolean(),
        label: Yup.string().when('isLabel', {
            is: true,
            then: () => Yup.string().required(t('survey.lRequired')),
        }),
        name: Yup.string().trim().test(
            "Whitespace check",
            `No white space allowed`,
            async (value) => {
                return value.indexOf(' ') === -1
            }
        ).required(t('survey.NRequired')),
        defaultValue: type === 5 && Yup.string().required('default value is required'),
        maxLength: Yup.number(),
        onlyNumber: Yup.boolean(),
        businessEmailValidation: Yup.boolean(),
    }
    if (field === 'text' || field === 'number' || field === 'email' || field === 'name' || type === 4 || type === 3) {
        shape.placeholder = Yup.string().trim().required(t('survey.PRequired'))
    }
    if (!(field === 'text' || field === 'number' || field === 'email' || field === 'name' || type === 4 || type === 6)) {
        shape.options = Yup.array().min(1, t('survey.builder.optionMinRequired')).of(Yup.string().trim().required(t('survey.builder.option')))
    }
    if (type === 6) {
        shape.label = Yup.string().required(t('survey.lRequired'))
    }

    const formSchema = Yup.object().shape(shape)

    //Formik
    let initialValues = {
        label: '',
        isLabel: false,
        name: '',
        defaultValue: ''
    }
    if (field === 'text' || field === 'number' || field === 'email' || field === 'name' || type === 4 || type === 3) {
        initialValues.placeholder = ''
    }
    if (!(field === 'text' || field === 'number' || field === 'email' || field === 'name' || type === 4 || type === 6)) {
        initialValues.options = []
    }
    initialValues.maxLength = ''
    initialValues.onlyNumber = false
    initialValues.businessEmailVerification = false

    const formik = useFormik({
        initialValues: initialValues,
        validationSchema: formSchema,
        onSubmit: (data) => {
            let payload = {
                language_code,
                label: data.label,
                name: data.name,
                options: null,
                fieldId: id,
                survey_id,
                type
            }

            if (type === 1) {
                if (field === 'text' || field === 'number' || field === 'email' || field === 'name') {
                    const tmpOption = { 0: { ...options['0'], type: field, required: options['0']?.required ?? false, placeholder: data.placeholder, maxLength: data.maxLength || null, onlyNumber: data.onlyNumber || false,  businessEmailVerification: data.businessEmailVerification || false } }
                    payload.options = tmpOption
                    dispatch(updateField(payload, handleCancel, handleError))
                } else if (field === 'radio' || field === 'checkbox') {
                    let tmpOptions = {}
                    data?.options?.forEach((opt, index) => {
                        tmpOptions[index] = { value: opt, type: field, required: options['0']?.required }
                    })
                    payload.options = tmpOptions
                    dispatch(updateField(payload, handleCancel, handleError))
                }
            } else if (type === 3) {
                const optObj = {}
                data?.options?.forEach((opt) => optObj[opt] = opt)
                const tmpOptions = { ...options, placeholder: data.placeholder, option: optObj }
                payload.options = tmpOptions
                dispatch(updateField(payload, handleCancel, handleError))
            } else if (type === 4) {
                const tmpOptions = { ...options, placeholder: data.placeholder, maxLength: data.maxLength || null }
                payload.options = tmpOptions
                dispatch(updateField(payload, handleCancel, handleError))
            } else if (type === 5) {
                const tmpOption = { 0: { ...options['0'], type: field, placeholder: data.placeholder, defaultValue: data.defaultValue } }
                payload.options = tmpOption
                dispatch(updateField(payload, handleCancel, handleError))
            } else if (type === 6) {
                payload.options = {}
                dispatch(updateField(payload, handleCancel, handleError))
            }
        }
    })

    const { values, handleChange, handleBlur, touched, errors, resetForm, setFieldValue, setFieldError, setFieldTouched, handleSubmit } = formik

    const canMutate = window.modulePermissions.M12.edit == 1

    //==> FUNCTIONALITIES <==
    const handleSwitchChange = (e) => {
        if (!canMutate) return
        setFieldValue('isLabel', e.target.checked)
        setFieldValue('label', '')
        setFieldTouched('label', false)
    }

    const handleSwitchChangeBusinessEmail = (e) => {
        if (!canMutate) return
        setFieldValue('businessEmailVerification', e.target.checked)
    }

    const handleCancel = (e) => {
        e?.preventDefault()
        setModalOpen(false)
        resetForm()
    }
    const handleError = (error) => {
        setFieldError('name', error)
    }

    const handleAddOption = (vals) => {
        if (!canMutate) return
        let tmpValues = [...vals]
        tmpValues.push('')
        setFieldValue('options', tmpValues)
    }

    const handleDeleteOption = (vals, index, e) => {
        e.preventDefault()
        if (!canMutate) return
        if (vals.length <= 1) {
            setFieldError('options', t('survey.builder.optionMinRequired'))
            return
        }
        let tmpValues = [...vals]
        tmpValues.splice(index, 1)
        setFieldValue('options', tmpValues)
    }


    useEffect(() => {
        if (data) {
            formik.setValues({
                ...formik.values,
                label: label ?? '',
                isLabel: type === 6 ? true : (label ? true : false),
                name: name ?? '',
                placeholder: options?.placeholder ?? '',
                maxLength: (type === 4 ? options?.maxLength : options?.[0]?.maxLength) ?? '',
                onlyNumber: options?.[0]?.onlyNumber ?? false,
                businessEmailVerification: options?.[0]?.businessEmailVerification ?? false,
            })
        }
    }, [data])

    //==> EFFECTS <==
    useEffect(() => {
        if (type === 6 && name && label) {
            setFieldValue('name', name)
            setFieldValue('label', label)
            setFieldValue('isLabel', true)
            return
        }
        if (name && options) {
            setFieldValue('name', name)
            setFieldValue('label', label ?? '')
            setFieldValue('isLabel', label ? true : false)

            if (field === 'text' || field === 'number' || field === 'email' || field === 'name' || type === 4 || type === 3) {
                if (type === 4 || type === 3) {
                    const { placeholder } = options
                    setFieldValue('placeholder', placeholder ?? '')
                } else {
                    const attributes = options['0'] ?? {}
                    setFieldValue('placeholder', attributes?.placeholder ?? '')
                }
            }

            if (type === 3 || field === 'radio' || field === 'checkbox') {
                if (type === 3) {
                    const { option } = options
                    const arr = Object.keys(option)?.map((opt) => opt)
                    setFieldValue('options', arr)
                } else {
                    const arr = Object.keys(options)?.map((opt) => options[opt].value)
                    setFieldValue('options', arr)
                }
            }

            if (type === 5) {
                const attributes = options['0'] ?? {}
                setFieldValue('defaultValue', attributes?.defaultValue ?? '')
                setFieldValue('placeholder', attributes?.placeholder ?? '')
            }
        }
    }, [label, name, options, field, modalOpen, type])

    return (
        <>
            <div className={`customvar-modal modal custom-modal fade edit-pop-modal ${modalOpen ? 'show' : ''}`} style={{ display: `${modalOpen ? 'block' : 'none'}` }} id='leadGenFieldEditModal' tabIndex={-1} aria-labelledby="editModalLabel" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered modal-lg">
                    <div className="modal-content">
                        <div className="modal-body">
                            <div className={`formcontainer`}>
                                <form onSubmit={(e) => { if (!canMutate) { e.preventDefault(); return; } handleSubmit(e); }}>
                                    <div className={`formvawrapper modal-edit ${style.wrapper}`}>
                                        <Card view="raised" className={`formstyle p-4 ${style.card}`} >
                                            <div className="edit-field-wrap p-0 mb-4 profile-wrapper">
                                                <h5>{type === 6 ? t('survey.builder.editLabel') : t('survey.builder.edit1')}</h5>
                                                <div className="field-wrap form-wrapper">
                                                    <div className="row">
                                                        <div className="col-md-6">
                                                            <div className="mb-3">
                                                                <label htmlFor="">{t('survey.builder.name')}</label>
                                                                <div className='inputtext position-relative'>
                                                                    <Input
                                                                        type='text'
                                                                        view='outlined'
                                                                        dense={false}
                                                                        color='default'
                                                                        label=''
                                                                        placeholder={t('survey.builder.name')}
                                                                        success={false}
                                                                        error={false}
                                                                        fill={true}
                                                                        errorHelperText={null}
                                                                        className={"formsinput"}
                                                                        name='name'
                                                                        value={values.name}
                                                                        readOnly={!canMutate}
                                                                        onChange={canMutate ? handleChange : undefined}
                                                                        onBlur={handleBlur}
                                                                    />
                                                                    {touched.name && errors.name && <div className='validation-msg'>{errors.name}</div>}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        {type === 6 ? (
                                                            <div className="col-md-12">
                                                                <div className="mb-3">
                                                                    <label htmlFor="">{t('survey.builder.labelContent')}</label>
                                                                    <div className='inputtext position-relative'>
                                                                        <textarea
                                                                            className="form-control"
                                                                            rows="3"
                                                                            placeholder={t('survey.builder.labelContentPlaceholder')}
                                                                            name='label'
                                                                            value={values.label}
                                                                            readOnly={!canMutate}
                                                                            onChange={canMutate ? handleChange : undefined}
                                                                            onBlur={handleBlur}
                                                                        />
                                                                        {touched.label && errors.label && <div className='validation-msg'>{errors.label}</div>}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                        <div className="col-md-6">
                                                            <div className="mb-3">
                                                                <div className="d-flex justify-content-between align-items-center">
                                                                    <label htmlFor="">{t('survey.builder.label')}</label>
                                                                    <div className="action-wrapper label-true">
                                                                        < Switch
                                                                            type='dense'
                                                                            color='default'
                                                                            label=''
                                                                            fill={false}
                                                                            rtl={false}
                                                                            leftPosition={false}
                                                                            className='SwitchCustom'
                                                                            name='isLabel'
                                                                            checked={values.isLabel}
                                                                            onChange={handleSwitchChange}
                                                                            disabled={!canMutate}
                                                                        />
                                                                    </div>
                                                                </div>
                                                                <div className='inputtext position-relative'>

                                                                    <Input
                                                                        type='text'
                                                                        view='outlined'
                                                                        dense={false}
                                                                        color='default'
                                                                        label=''
                                                                        placeholder={t('survey.builder.label')}
                                                                        success={false}
                                                                        error={false}
                                                                        fill={true}
                                                                        errorHelperText={null}
                                                                        className={"formsinput"}
                                                                        disabled={!values.isLabel || !canMutate}
                                                                        name='label'
                                                                        value={values.label}
                                                                        onChange={canMutate ? handleChange : undefined}
                                                                        onBlur={handleBlur}
                                                                    />
                                                                    {touched.label && errors.label && <div className='validation-msg'>{errors.label}</div>}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        )}
                                                        {(field === 'text' || field === 'number' || field === 'email' || field === 'name' || type === 4 || type === 3 || type === 5) &&
                                                                <div className="col-md-6">
                                                                    <div className="mb-3">
                                                                        <label htmlFor="">{t('survey.builder.place')}</label>
                                                                        <div className='inputtext position-relative'>

                                                                            <Input
                                                                                type='text'
                                                                                view='outlined'
                                                                                dense={false}
                                                                                color='default'
                                                                                label=''
                                                                                placeholder={t('survey.builder.place')}
                                                                                success={false}
                                                                                error={false}
                                                                                fill={true}
                                                                                errorHelperText={null}
                                                                                className={"formsinput"}
                                                                                name='placeholder'
                                                                                value={values.placeholder}
                                                                                readOnly={!canMutate}
                                                                                onChange={canMutate ? handleChange : undefined}
                                                                                onBlur={handleBlur}
                                                                            />
                                                                            {touched.placeholder && errors.placeholder && <div className='validation-msg'>{errors.placeholder}</div>}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                        }
                                                        {(field === 'text' || field === 'number' || field === 'email' || field === 'name' || type === 4) &&
                                                                <div className="col-md-6">
                                                                    <div className="mb-3">
                                                                            <label htmlFor="maxLength">{t('survey.builder.maxLength')}</label>
                                                                            <Input
                                                                                type="number"
                                                                                placeholder={t('survey.builder.enter_max_length')}
                                                                                success={false}
                                                                                error={false}
                                                                                fill={true}
                                                                                className={"formsinput"}
                                                                                name='maxLength'
                                                                                value={values.maxLength}
                                                                                readOnly={!canMutate}
                                                                                onChange={canMutate ? handleChange : undefined}
                                                                                onBlur={handleBlur}
                                                                                view='outlined'
                                                                                dense={false}
                                                                                color='default'
                                                                                label=''
                                                                            />
                                                                    </div>
                                                                </div>
                                                        }
                                                        {(field === 'text' || field === 'number') &&
                                                                <div className="col-md-6">
                                                                    <div className="mb-3">
                                                                        <div className="d-flex align-items-center action-wrapper label-true"> 
                                                                            <Switch
                                                                                type='dense'
                                                                                color='default'
                                                                                label=""
                                                                                fill={false}
                                                                                rtl={false}
                                                                                leftPosition={false}
                                                                                className='SwitchCustom'
                                                                                name='onlyNumber'
                                                                                checked={values.onlyNumber}
                                                                                onChange={handleChange}
                                                                                dense={false}
                                                                                disabled={!canMutate}
                                                                            />
                                                                            <label className='ms-2 mb-0'>{t('survey.builder.only_numbers')}</label>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                        }
                                                        {(field === 'email') &&
                                                                <div className="col-md-6">
                                                                    <div className="d-flex  align-items-center gap-3 ">
                                                                        <label htmlFor="" className='mb-0'>{t('survey.builder.businessValidation')}</label>
                                                                        <div className="action-wrapper label-true">
                                                                            < Switch
                                                                                type='dense'
                                                                                color='default'
                                                                                label=''
                                                                                fill={false}
                                                                                rtl={false}
                                                                                leftPosition={false}
                                                                                className='SwitchCustom'
                                                                                name='businessEmailVerification'
                                                                                checked={values.businessEmailVerification}
                                                                                onChange={handleSwitchChangeBusinessEmail}
                                                                                disabled={!canMutate}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                        }
                                                        {(type === 5) && <div className="col-md-6">
                                                            <div className="mb-3">
                                                                <label htmlFor="">{t('survey.builder.value')}</label>
                                                                <div className='inputtext position-relative'>
                                                                    <Input
                                                                        type='text'
                                                                        view='outlined'
                                                                        dense={false}
                                                                        color='default'
                                                                        label=''
                                                                        placeholder={t('survey.builder.value')}
                                                                        success={false}
                                                                        error={false}
                                                                        fill={true}
                                                                        errorHelperText={null}
                                                                        className={"formsinput"}
                                                                        name='defaultValue'
                                                                        value={values.defaultValue}
                                                                        readOnly={!canMutate}
                                                                        onChange={canMutate ? handleChange : undefined}
                                                                        onBlur={handleBlur}
                                                                    />
                                                                    {touched.defaultValue && errors.defaultValue && <div className='validation-msg'>{errors.defaultValue}</div>}
                                                                </div>
                                                            </div>
                                                        </div>}
                                                    </div>
                                                </div>
                                                {!(field === 'text' || field === 'number' || field === 'email' || field === 'name' || field === 'hidden' || type === 4 || type === 5 || type === 6) && <div className="options-wrap">
                                                    <label htmlFor="">{t('survey.builder.option')}</label>
                                                    {typeof errors.options === 'string' && <div className='validation-msg'>{errors.options}</div>}
                                                    <div className="form-field-wrapper">
                                                        <div className="row">
                                                            {
                                                                values.options?.map((opt, index) => {
                                                                    return (
                                                                        <div className="col-md-6" key={index}>
                                                                            <div className="mb-3 d-flex align-items-center">
                                                                                <div className="flex-grow-1 pe-2 position-relative">
                                                                                    <Input
                                                                                        type='text'
                                                                                        view='outlined'
                                                                                        dense={false}
                                                                                        color='default'
                                                                                        label=''
                                                                                        placeholder={t('survey.builder.option')}
                                                                                        success={false}
                                                                                        error={false}
                                                                                        fill={true}
                                                                                        errorHelperText={null}
                                                                                        className={"formsinput"}
                                                                                        value={opt}
                                                                                        name={`options.${index}`}
                                                                                        readOnly={!canMutate}
                                                                                        onChange={canMutate ? handleChange : undefined}
                                                                                        onBlur={handleBlur}
                                                                                    />
                                                                                    {touched.options && touched.options[index] && errors.options && errors.options[index] && <div className='validation-msg'>{errors.options[index]}</div>}
                                                                                </div>
                                                                                <div className="action-buttons ">
                                                                                    {window.modulePermissions.M12.del == 1 && (
                                                                                        <button
                                                                                            className='action-btn delete'
                                                                                            disabled={values.options?.length <= 1 || !canMutate}
                                                                                            style={values.options?.length <= 1 || !canMutate ? { opacity: 0.4, cursor: 'not-allowed' } : {}}
                                                                                            onClick={(e) => handleDeleteOption(values.options, index, e)}
                                                                                        >
                                                                                            <Svg name="section-delete" />
                                                                                        </button>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    )
                                                                })
                                                            }

                                                        </div>
                                                    </div>

                                                    <div className="btn-wrap">
                                                        <button type="button" className="add-option d-inline-flex align-items-center" disabled={!canMutate} onClick={() => handleAddOption(values.options)}>
                                                            <Svg name="add-option" /> {t('survey.builder.addOptions')}
                                                        </button>
                                                    </div>
                                                </div>}
                                            </div>
                                            <div className="button-wrap d-flex justify-content-end">
                                                <button type="button" className="btn btn-bordered" data-bs-dismiss="modal" onClick={(e) => handleCancel(e)}>{t('survey.customFormula.cancel')}</button>
                                                {canMutate && <button type="submit" className="btn btn-primary ms-4">{t('survey.builder.save')}</button>}
                                            </div>
                                        </Card>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default EditModal